import { Component, OnInit } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Global } from '../../../../app.global';
import { environment } from '../../../../../environments/environment';

interface AvatarOption {
  id: string;
  backgroundImage?: string;
  backgroundColor?: string;
}

interface DefaultStages {
  progressionPathA: string | null;
  progressionPathB: string | null;
  progressionPathC: string | null;
  progressionPathD: string | null;
  progressionPathE: string | null;
  progressionPathF: string | null;
  progressionPathG: string | null;
}

interface UserSettings {
  autoPinPcnBlock: boolean;
  defaultStages: DefaultStages;
}

interface PcnStageOption {
  value: string;
  label: string;
}

interface UserSettingsResponse {
  profile: {
    avatarId: string;
  };
  settings: UserSettings;
}

type ProgressionPath = 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G';

@Component({
  selector: 'app-user-settings',
  templateUrl: './user-settings.component.html',
  styleUrls: ['./user-settings.component.scss'],
})
export class UserSettingsComponent implements OnInit {
  public activeTab: 'profile' | 'settings' = 'settings';

  public profileAvatars: AvatarOption[] = [
    { id: 'avatar-1', backgroundImage: 'linear-gradient(135deg, #f97316, #1d4ed8)' },
    { id: 'avatar-2', backgroundImage: 'radial-gradient(circle at 0 0, #ec4899, #22c55e)' },
    { id: 'avatar-3', backgroundImage: 'linear-gradient(135deg, #6366f1, #0ea5e9)' },
    {
      id: 'avatar-4',
      backgroundImage:
        'repeating-linear-gradient(45deg, #0f172a, #0f172a 10px, #38bdf8 10px, #38bdf8 20px)',
    },
    { id: 'avatar-5', backgroundImage: 'radial-gradient(circle at 20% 20%, #e11d48, #1e293b)' },
    {
      id: 'avatar-6',
      backgroundImage:
        'repeating-linear-gradient(-45deg, #22c55e, #22c55e 8px, #bef264 8px, #bef264 16px)',
    },
    { id: 'avatar-7', backgroundImage: 'linear-gradient(135deg, #facc15, #f97316)' },
    { id: 'avatar-8', backgroundImage: 'radial-gradient(circle at 80% 0, #a855f7, #0f172a)' },
  ];

  public selectedAvatarId = 'avatar-1';

  public pcnStagesByPath: Record<ProgressionPath, PcnStageOption[]> = {
    A: [
      { value: 'pre-NTO discount', label: 'pre-NTO discount' },
      { value: 'pre-NTO full charge', label: 'pre-NTO full charge' },
      { value: 'NTO sent', label: 'NTO sent' },
      { value: 'CC sent', label: 'CC sent' },
      { value: 'OfR sent', label: 'OfR sent' },
      { value: 'bailiffs', label: 'bailiffs' },
    ],
    B: [
      { value: 'pre-CC discount', label: 'pre-CC discount' },
      { value: 'pre-CC full charge', label: 'pre-CC full charge' },
      { value: 'CC sent', label: 'CC sent' },
      { value: 'OfR sent', label: 'OfR sent' },
      { value: 'bailiffs', label: 'bailiffs' },
    ],
    C: [
      { value: 'pre-CC discount', label: 'pre-CC discount' },
      { value: 'pre-CC full charge', label: 'pre-CC full charge' },
      { value: 'CC sent', label: 'CC sent' },
      { value: 'OfR sent', label: 'OfR sent' },
      { value: 'bailiffs', label: 'bailiffs' },
    ],
    D: [
      { value: 'pre-CC discount', label: 'pre-CC discount' },
      { value: 'pre-CC full charge', label: 'pre-CC full charge' },
      { value: 'CC sent', label: 'CC sent' },
      { value: 'OfR sent', label: 'OfR sent' },
      { value: 'bailiffs', label: 'bailiffs' },
    ],
    E: [
      { value: 'pre-EN discount', label: 'pre-EN discount' },
      { value: 'pre-EN full charge', label: 'pre-EN full charge' },
      { value: 'EN sent', label: 'EN sent' },
      { value: 'CC sent', label: 'CC sent' },
      { value: 'OfR sent', label: 'OfR sent' },
      { value: 'bailiffs', label: 'bailiffs' },
    ],
    F: [
      { value: 'pre-CC discount', label: 'pre-CC discount' },
      { value: 'pre-CC full charge', label: 'pre-CC full charge' },
      { value: 'CC sent', label: 'CC sent' },
      { value: 'OfR sent', label: 'OfR sent' },
      { value: 'bailiffs', label: 'bailiffs' },
    ],
    G: [
      { value: 'warning', label: 'warning' },
      { value: 'pre-CC discount', label: 'pre-CC discount' },
      { value: 'pre-CC full charge', label: 'pre-CC full charge' },
      { value: 'CC sent', label: 'CC sent' },
      { value: 'OfR sent', label: 'OfR sent' },
      { value: 'bailiffs', label: 'bailiffs' },
    ],
  };

  public userSettings: UserSettings = {
    autoPinPcnBlock: false,
    defaultStages: {
      progressionPathA: 'pre-NTO discount',
      progressionPathB: 'pre-CC discount',
      progressionPathC: 'pre-CC discount',
      progressionPathD: 'pre-CC discount',
      progressionPathE: 'pre-EN discount',
      progressionPathF: 'pre-CC discount',
      progressionPathG: 'warning',
    },
  };

  public enableThemeSetting = false;
  public isSaving = false;
  public saveSucceeded = false;
  public saveError: string | null = null;

  private readonly settingsUrl = `${environment.apiPath}api/account/userSettings`;

  constructor(
    public global: Global,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.loadSettings();
  }

  public getStages(path: ProgressionPath): PcnStageOption[] {
    return this.pcnStagesByPath[path] || [];
  }

  public selectAvatar(avatarId: string): void {
    this.selectedAvatarId = avatarId;
  }

  public setTab(tab: 'profile' | 'settings'): void {
    this.activeTab = tab;
  }

  public saveSettings(): void {
    const payload = {
      profile: {
        avatarId: this.selectedAvatarId,
      },
      settings: this.userSettings,
    };

    const options = this.getAuthOptions();

    this.isSaving = true;
    this.saveSucceeded = false;
    this.saveError = null;

    this.http.post<void>(this.settingsUrl, payload, options).subscribe({
      next: () => {
        this.isSaving = false;
        this.saveSucceeded = true;
      },
      error: (err) => {
        console.error(err);
        this.isSaving = false;
        this.saveSucceeded = false;
        this.saveError = 'could not save your settings. please try again.';
      },
    });
  }

  private getAuthOptions(): { headers?: HttpHeaders } {
    const rmAccessToken = localStorage.getItem('rm_access_token') || '';
    if (!rmAccessToken) {
      return {};
    }

    return {
      headers: new HttpHeaders({
        Authorization: `Bearer ${rmAccessToken}`,
        Accept: 'application/json',
      }),
    };
  }

  private loadSettings(): void {
    const options = this.getAuthOptions();

    this.http.get<UserSettingsResponse | null>(this.settingsUrl, options).subscribe({
      next: (response) => {
        if (!response) {
          return;
        }

        this.selectedAvatarId = response.profile?.avatarId || 'avatar-1';

        if (response.settings) {
          this.userSettings.autoPinPcnBlock = !!response.settings.autoPinPcnBlock;

          const ds = (response.settings.defaultStages || {}) as DefaultStages;
          this.userSettings.defaultStages = {
            progressionPathA:
              ds.progressionPathA ?? this.userSettings.defaultStages.progressionPathA,
            progressionPathB:
              ds.progressionPathB ?? this.userSettings.defaultStages.progressionPathB,
            progressionPathC:
              ds.progressionPathC ?? this.userSettings.defaultStages.progressionPathC,
            progressionPathD:
              ds.progressionPathD ?? this.userSettings.defaultStages.progressionPathD,
            progressionPathE:
              ds.progressionPathE ?? this.userSettings.defaultStages.progressionPathE,
            progressionPathF:
              ds.progressionPathF ?? this.userSettings.defaultStages.progressionPathF,
            progressionPathG:
              ds.progressionPathG ?? this.userSettings.defaultStages.progressionPathG,
          };
        }
      },
      error: (err) => {
        console.error(err);
      },
    });
  }
}
